import { Dispatch, PayloadAction, createSlice } from "@reduxjs/toolkit";
import { TRequestStatus } from "@models/msc.model";
import usersService from "@/services/users.service";
import { RootState } from "..";
import { TUpdateUser, TUser } from "@/models/user.model";

type UsersState = {
  initialized: boolean;
  status: TRequestStatus;
  users: TUser[];
};

//-----THUNK FUNCTIONS-----

const initialState: UsersState = {
  initialized: false,
  status: "IDLE",
  users: [],
};

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    startLoading(state) {
      state.status = "PENDING";
    },
    rollback(state, actions) {
      state.status = "IDLE";
      state.users = actions.payload;
    },
    gotUsers(state, action) {
      state.users = action.payload;
      state.status = "IDLE";
      state.initialized = true;
    },
    userUpdated(state, action: PayloadAction<TUpdateUser>) {
      state.users = state.users.map((i) =>
        i.id === action.payload.id ? { ...i, ...action.payload, roles: action.payload.roles } : i
      );
    },
    userRolesUpdated(state, action: PayloadAction<{ id: number; roles: number[] }>) {
      state.users = state.users.map((i) => (i.id === action.payload.id ? { ...i, roles: action.payload.roles } : i));
    },
    userDeleted(state, action) {
      state.users = state.users.filter((i) => i.id !== action.payload);
    },
    userInvited(state, action: PayloadAction<TUser>) {
      state.users = [...state.users, action.payload];
    },
  },
});

export const usersActions = usersSlice.actions;

// ----------------------------

export const getAllUsers = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const {
    users: { users, initialized },
  } = getState();

  if (initialized) return users;

  dispatch(usersSlice.actions.startLoading());
  const response = await usersService.getAllUsers();
  dispatch(usersSlice.actions.gotUsers(response));
  return response;
};

export const updateUser = (user: TUpdateUser) => async (dispatch: Dispatch) => {
  const response = await usersService.updateUser(user);
  dispatch(usersSlice.actions.userUpdated(user));
  return response;
};

export const deleteUser = (userId: number, reassignToUserId?: number) => async (dispatch: Dispatch) => {  
  const response = await usersService.deleteuser(userId, reassignToUserId);
  dispatch(usersSlice.actions.userDeleted(userId));
  return response;
};

export const inviteUser = (user: { email: string; roles: number[] }) => async (dispatch: Dispatch) => {
  const response = await usersService.inviteUser(user);
  dispatch(usersSlice.actions.userInvited(response));
  return response;
};

// ----------------------------

export default usersSlice.reducer;
